import './app.css';
import { Fragment, useEffect, useState, useRef, useCallback } from 'react';
import Unity, { UnityContext } from 'react-unity-webgl';
import ReactTfliteInception from './ReactTfliteInception';
import { OutputValue } from '@hotg-ai/forge';
import _ from "lodash"

import LoadingIcons from 'react-loading-icons'

function useThrottle(cb, delay) {
  const options = { leading: true, trailing: false }; // add custom lodash options
  const cbRef = useRef(cb);
  // use mutable ref to make useCallback/throttle not depend on `cb` dep
  useEffect(() => { cbRef.current = cb; });
  return useCallback(
    _.throttle((...args) => cbRef.current(...args), delay, options),
    [delay]
  );
}
interface Vector2 {
  x: number;
  y: number;
}

function useDebounce(value, delay) {
  // State and setters for debounced value
  const [debouncedValue, setDebouncedValue] = useState(value);
  useEffect(
    () => {
      // Update debounced value after delay
      const handler = setTimeout(() => {
        setDebouncedValue(value);
      }, delay);
      // Cancel the timeout if value changes (also on delay change or unmount)
      // This is how we prevent debounced value from updating if value is changed ...
      // .. within the delay period. Timeout gets cleared and restarted.
      return () => {
        clearTimeout(handler);
      };
    },
    [value, delay] // Only re-call effect if value or delay changes
  );
  return debouncedValue;
}

// This is the context that Unity will use to communicate with the React app.
const unityContext = new UnityContext({
  productName: 'Unicorn',
  companyName: 'HOTG.ai',
  // The url's of the Unity WebGL runtime, these paths are public and should be
  // accessible from the internet and relative to the index.html.
  loaderUrl: 'unicornity/unicorn_web.loader.js',
  dataUrl: 'unicornity/unicorn_web.data',
  frameworkUrl: 'unicornity/unicorn_web.framework.js',
  codeUrl: 'unicornity/unicorn_web.wasm',
  //streamingAssetsUrl: 'unitybuild/2020.1/streamingassets',
  // Additional configuration options.
  webglContextAttributes: {
    preserveDrawingBuffer: true,
  },
});

// This is the React component that will be rendering the Unity app.
function App() {
  // The app's state.
  const [isUnityMounted, setIsUnityMounted] = useState<boolean>(true);
  const [isLoaded, setIsLoaded] = useState<boolean>(false);
  const [progression, setProgression] = useState<number>(0);
  const [isAILoaded, setAILoaded] = useState<boolean>(false);


  //Data which comes from webcam
  const [data, setData] = useState<OutputValue[]>([]);
  const debouncedData = useDebounce(data, 500);

  // When the component is mounted, we'll register some event listener.
  useEffect(() => {
    unityContext.on('canvas', handleOnUnityCanvas);
    unityContext.on('progress', handleOnUnityProgress);
    unityContext.on('loaded', handleOnUnityLoaded);
    // unityContext.on('RotationDidUpdate', handleOnUnityRotationDidUpdate);
    // unityContext.on('ClickedPosition', handleOnUnityClickedPosition);
    // unityContext.on('Say', handleOnUnitySayMessage);
    // When the component is unmounted, we'll unregister the event listener.
    return function () {
      unityContext.removeAllEventListeners();
    };
  }, []);

  // When the rotation speed has been updated, it will be sent to Unity.


  // Built-in event invoked when the Unity canvas is ready to be interacted with.
  function handleOnUnityCanvas(canvas: HTMLCanvasElement) {
    canvas.setAttribute('role', 'unityCanvas');
  }

  // Built-in event invoked when the Unity app's progress has changed.
  function handleOnUnityProgress(progression: number) {
    setProgression(progression);
  }

  // Built-in event invoked when the Unity app is loaded.
  function handleOnUnityLoaded() {
    setIsLoaded(true);
  }


  // Event invoked when the user clicks the button, the unity container will be
  // mounted or unmounted depending on the current mounting state.

  const throttledCb = useThrottle(() => {
    const predictions = data.flatMap((v) => [...v.elements]);

    if (predictions.toString() === 'up') {
      // handleOnClickIncreaseSpeed();

      unityContext.send('Main Camera', 'SetInt', `animation,${3}`);
    } else if (predictions.toString() === 'down') {
      unityContext.send('Main Camera', 'SetInt', `animation,${10}`);
    } else {
      unityContext.send('Main Camera', 'SetInt', `animation,${Math.random() > 0.5 ? 1 : 2}`);
    }

  }, 100);

  useEffect(() => throttledCb, [data]);

  // This is the React component that will be rendering the Unity app.
  return (
    <Fragment>
      <div>
        <a href="https://hotg.ai"><img src='logo.png' alt='HOTG-UNITY-GAME' /></a>
      </div>

      {(!isLoaded || !isAILoaded) && (
        <div style={{ position: "absolute", display:"flex", flexDirection:"column", backgroundImage: "url('splash.jpeg')", top: "0px", left: "0px", right: "0px", bottom: "0px", width: "100vw", height: "100vh", margin: "0px", padding: "10px", backgroundColor: "black", zIndex: 999, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <div><LoadingIcons.Bars color='#FF00FF'/>
          </div>
          <br />
          <div style={{
            fontFamily: 'Lato',
            fontWeight: 400,
            background: '#000000CC',
            padding: '5px',
            borderRadius: '5px',
            backgroundPosition: 'center',
            backgroundSize: 'contain',
            textAlign: 'center'
      
          }}>Best used with wifi!<br /> Point up or shoot sideways to interact with the 3D NFT</div>
        </div>

      )}

      <div className='home' style={{marginTop:"10%"}}>
        <div className='home-content'>
          {/* The Unity container */}
          {isUnityMounted === true && (
            <Fragment>
              <div className='unity-container'>
                {/* The loading screen will be displayed here. */}
                {isLoaded === false && (
                  <div className='loading-overlay'>
                    <div className='progress-bar'>
                      <div
                        className='progress-bar-fill'
                        style={{ width: progression * 100 + '%' }}
                      />
                    </div>
                  </div>
                )}
                {/* The Unity app will be rendered here. */}
                <Unity className='unity-canvas' unityContext={unityContext} />
              </div>
              {/* Displaying some output values */}
            </Fragment>
          )}
        </div>
        <ReactTfliteInception data={data} setData={setData} setLoaded={setAILoaded} />
        <div>
          <a className="bt bt-break" href="mailto:kartik@hotg.ai?subject=I want to make some cool NFTs">
            Contact Us
          </a>
        </div>
        <p>
          HOTG.ai creates portable AI containers that can be decentralized to create immersive and interactive NFT 
          assets. Contact us to learn more.
        </p>
      </div>

    </Fragment>
  );
}

export default App;
